/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

$(document).ready(function() {
  var drawerRight = $('.ele-drawer-right');
  var cart_list = $('.view_all_styles');

  cart_list.click(function() {
    $(this).toggleClass('active');      
    $("#ol").toggleClass("overlay_drawer");
    $('body').toggleClass('ele_cart-drawer-pushtoleft');
    drawerRight.toggleClass('ele_cart-drawer-open');
  });

  $(document).on('click', '.overlay_drawer, .ele_cart-drawer-close-btn', function() {
    console.log("hereeee")
    $("#ol").removeClass("overlay_drawer");
    $('body').removeClass('ele_cart-drawer-pushtoleft');
    drawerRight.removeClass('ele_cart-drawer-open');
    drawerRightPacks.removeClass('ele_cart-drawer-open');
  });


    var swiper = new Swiper(".mySwiper", {
      slidesPerView: 2.8,
      spaceBetween: 16,
      scrollbar: {
        el: ".swiper-scrollbar",
        hide: true,
      },
      navigation: {
        nextEl: ".gallery_tech-specs-next",
        prevEl: ".gallery_tech-specs-prev",
      },
      breakpoints: {
        0: {
          slidesPerView: 1.1,
          spaceBetween: 12,
        },
        768: {
          slidesPerView: 1.8,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 2.8,
          spaceBetween: 16,
        },
      },
    });

  $(document).on('click', '.tab_btn_ele', function(e) {
    e.preventDefault();
    e.stopPropagation();
    $('.tab_btn_ele').removeClass('active');    

    var atr = $(this).attr('datatab');
    $('.featured_collections_ele').hide();
    $('.featured_collections_ele.'+atr).fadeIn();
    $(this).addClass('active');      
  });
  
  $(".collection_link_cstm").mouseover(function (e) {
    $('.collection_link_cstm').removeClass('active');
    $('.mach-hypertext-image-sub-content').hide();
    var src = $(this).attr('src');
    var title = $(this).attr('title');
    var desc = $(this).attr('desc');
    var url = $(this).attr('url');
    console.log('srccc', src)
    $('.mach-hypertext-image-sub-content img').attr("src", src);
    $('.mach-hypertext-image-sub-content .content_positioned h2').html(title);
    $('.mach-hypertext-image-sub-content .content_positioned p').html(desc);
    $('.mach-hypertext-image-sub-content .content_positioned a.box_url').attr('href', url);
    // setTimeout(function() {
      $('.mach-hypertext-image-sub-content').show();
    // }, 100)
    $(this).addClass('active');
  });  

  var showChar = 100;
  var ellipsestext = "...";
  var moretext = "Read more";
  var lesstext = "Read less";

  $(".metaobjects_value_wrapper_ele_in .rte").each(function () {
    var content = $(this).html();

    if (content.length > showChar) {
      var c = content.substr(0, showChar);
      var h = content.substr(showChar, content.length - showChar);

      var html = c + '<span class="moreellipses">' + ellipsestext + '&nbsp;</span><span class="morecontent"><span>' + h + '</span>&nbsp;&nbsp;<a href="" class="morelink">' + moretext + '</a></span>';

      $(this).html(html);
    }
  });

  $(".metaobjects_value_wrapper_ele_in .rte").on("click", ".morelink", function (e) {
    e.preventDefault();
    var $this = $(this);
    if ($this.hasClass("less")) {
      $this.removeClass("less");
      $this.html(moretext);
    } else {
      $this.addClass("less");
      $this.html(lesstext);
    }
    // $this.prev().toggle(); // toggle ellipses
    $this.parent().prev().toggle(); // toggle preview text
    $this.parent().find("span").toggle(); // toggle full text
  });
  $('button.button.btn.find_my_size').click(function() {
    console.log('fdfdfd')
    $('fieldset.option-selector[data-option="Size"] article button').trigger('click')
  })

  /* Mobile menu gender tabs (Men / Women) */
  document.addEventListener('click', function (e) {
    var tab = e.target.closest && e.target.closest('.mobile-gender-tab');
    if (!tab) return;
    e.preventDefault();
    var nav = tab.closest('.mobile-gender-nav');
    if (!nav) return;
    var gender = tab.getAttribute('data-gender');
    nav.querySelectorAll('.mobile-gender-tab').forEach(function (t) {
      var active = t === tab;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    nav.querySelectorAll('.mobile-gender-panel').forEach(function (p) {
      p.classList.toggle('is-active', p.getAttribute('data-gender') === gender);
    });
    // Mach: drive the Mens/Womens image at the bottom of the drawer to match the active tab.
    var drawer = nav.closest('.mobile-navigation-drawer');
    if (drawer) drawer.setAttribute('data-active-gender', gender);
  });

  /* Mobile menu cart icon opens the cart drawer (not the cart page) */
  document.addEventListener('click', function (e) {
    var cartBtn = e.target.closest && e.target.closest('.mobile-nav-cart');
    if (!cartBtn) return;
    var drawer = document.querySelector('.js-cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return; // no drawer -> follow link to cart page
    e.preventDefault();
    // close the mobile menu
    document.body.classList.remove('reveal-mobile-nav', 'reveal-mobile-nav--revealed');
    setTimeout(function () {
      document.body.classList.remove('enable-mobile-nav-transition');
    }, 750);
    // open the cart drawer
    drawer.open(cartBtn);
  });

  /* Cart drawer: tap a line-item image to open the product image gallery/zoom viewer.
     Reuses the theme's <gallery-viewer> modal (see snippet 'gallery-viewer' + main.js). */
  if (!window.customElements.get('cart-item-zoom')) {
    window.customElements.define('cart-item-zoom', class extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;
        this.trigger = this.querySelector('.cart-item__zoom-trigger');
        this.template = this.querySelector('.js-media-zoom-template');
        if (!this.trigger || !this.template) return;
        this.trigger.addEventListener('click', this.onClick.bind(this));
      }

      disconnectedCallback() {
        // Cart drawer re-renders on every quantity change; drop our appended modal so
        // stale viewers don't pile up in <body>.
        if (this.modal && this.modal.parentElement) this.modal.remove();
      }

      onClick(evt) {
        evt.preventDefault();
        if (!this.modal) {
          this.modal = this.template.content.firstElementChild.cloneNode(true);
          document.body.appendChild(this.modal);
        }
        this.modal.open(this.trigger);
        var viewer = this.modal.querySelector('gallery-viewer');
        viewer.init(this.trigger.getAttribute('data-zoom-url'));
        viewer.focus();
      }
    });
  }

})




